import { createContext, useContext } from 'react'
import type { ModalContexts, ModalId } from '../types'

interface ModalContextValue {
  id: string
  context?: any
  close: () => void
  confirm?: boolean
}

const ModalContext = createContext<ModalContextValue | undefined>(undefined)

// Modal ID is required for type safety
export function useModalContext<K extends ModalId>(
  modalId: K,
): {
  id: K
  context: ModalContexts[K]
  close: () => void
  confirm?: boolean
} {
  const ctx = useContext(ModalContext)
  if (!ctx) {
    throw new Error('useModalContext must be used within a ModalContext.Provider')
  }

  // Optional: warn if modal ID doesn't match (helpful for debugging)
  if (modalId && ctx.id !== modalId) {
    console.warn(`Modal context mismatch: expected ${modalId}, got ${ctx.id}`)
  }

  return {
    id: ctx.id as K,
    context: ctx.context as ModalContexts[K],
    close: ctx.close,
    confirm: ctx.confirm,
  }
}

export { ModalContext }
